import React, { useState } from 'react';
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { formatPrice, LoadingGrid } from '../../../../../rbts/helpers';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import { useObtenerDiasPorEvento } from '../../cortesias/hooks/CortesiasHook';
import {
  useReporteVentas,
  TicketConVentas,
  DetalleVenta,
  ResumenVenta,
} from '../hooks/ReporteVentasHook';

const formatFecha = (fechaStr: string) => {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TablaDetalle: React.FC<{ detalle: DetalleVenta[] }> = ({ detalle }) => {
  if (!detalle || detalle.length === 0) {
    return (
      <tr>
        <td colSpan={10} className="text-center text-muted py-3 fst-italic">
          Sin ventas registradas para este ticket
        </td>
      </tr>
    );
  }
  return (
    <>
      {detalle.map((v, idx) => (
        <tr key={`${v.venta_id}-${idx}`} className="align-middle">
          <td className="text-muted fs-7">{v.venta_id}</td>
          <td className="fs-7">{formatFecha(v.fecha_hora_venta)}</td>
          <td className="fw-semibold fs-7">{v.producto ?? '—'}</td>
          <td className="text-center fs-7">{v.cantidad}</td>
          <td className="text-end fs-7">
            {formatPrice(v.precio_unitario ?? 0, 'es-MX', 'MXN', 2, 2)}
          </td>
          <td className="text-end fw-bold fs-7">
            {formatPrice(v.total_venta ?? 0, 'es-MX', 'MXN', 2, 2)}
          </td>
          <td className="text-center fs-7">
            <span className={`badge ${v.forma_pago === 'Efectivo' ? 'badge-light-warning' : 'badge-light-primary'}`}>
              {v.forma_pago}
            </span>
          </td>
          <td className="fs-7">{v.punto_venta ?? '—'}</td>
          <td className="fs-7 text-muted">{v.tipo_punto_venta ?? '—'}</td>
        </tr>
      ))}
    </>
  );
};

const FilaResumen: React.FC<{ resumen: ResumenVenta; folioOrigen?: string | null }> = ({ resumen, folioOrigen }) => {
  const esTransferido = resumen.activo === 0 && !!resumen.folio_nuevo;
  return (
    <tr className="tc-report-subtotal">
      <td colSpan={2} className="fs-7 ps-3 tc-report-subtotal-label">
        {formatFecha(resumen.fecha_primera_venta)}
      </td>
      <td className="fs-7 fw-bolder tc-report-subtotal-label">
        RESUMEN
      </td>
      <td className="text-center fs-7">
        <span className="badge badge-light-primary">
          {resumen.evento ?? '—'}
        </span>
      </td>
      <td colSpan={2} className="text-end fs-7 tc-report-subtotal-label">
        Recarga:&nbsp;
        <span className="tc-amount-positive">
          {formatPrice(resumen.saldo_recarga_caja ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-center fs-7 tc-report-subtotal-label">
        Consumido:&nbsp;
        <span className="tc-amount-negative">
          {formatPrice(resumen.total_consumido_puesto ?? 0, 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
      <td className="text-center fs-7">
        {esTransferido && (
          <span className="badge badge-light-warning fw-bold">
            Transferido
          </span>
        )}
        {!esTransferido && (resumen.transferido ?? 0) > 0 && (
          <span className="fs-7 tc-report-subtotal-label">
            {folioOrigen ? `Transferido del Folio ${folioOrigen}:` : 'Transferido:'}&nbsp;
            <span className="tc-amount-positive fw-bold">
              {formatPrice(resumen.transferido, 'es-MX', 'MXN', 2, 2)}
            </span>
          </span>
        )}
      </td>
      <td colSpan={2} className="text-end fs-7 tc-report-subtotal-label">
        Saldo Actual:&nbsp;
        <span className="tc-amount-positive">
          {formatPrice(esTransferido ? 0 : (resumen.saldo_actual ?? 0), 'es-MX', 'MXN', 2, 2)}
        </span>
      </td>
    </tr>
  );
};

const Vista: React.FC = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [idsDia, setIdsDia] = useState<number[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);

  const { list: listadosDeDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);
  const { list, loadingRequest } = useReporteVentas(idsDia);

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Reporte de Ventas');

      ws.columns = [
        { header: 'Folio', key: 'folio', width: 12 },
        { header: 'Venta ID', key: 'venta_id', width: 10 },
        { header: 'Fecha/Hora', key: 'fecha_hora_venta', width: 20 },
        { header: 'Producto', key: 'producto', width: 30 },
        { header: 'Cantidad', key: 'cantidad', width: 10 },
        { header: 'Precio Unit.', key: 'precio_unitario', width: 14 },
        { header: 'Total Venta', key: 'total_venta', width: 14 },
        { header: 'Forma Pago', key: 'forma_pago', width: 14 },
        { header: 'Punto de Venta', key: 'punto_venta', width: 20 },
        { header: 'Tipo', key: 'tipo', width: 12 },
        { header: 'Transferencia', key: 'transferencia', width: 20 },
      ];

      ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };

      list.forEach((ticket) => {
        const esTransferido = ticket.resumen?.activo === 0 && !!ticket.resumen?.folio_nuevo;
        const folioOrigen = list.find(t => t.resumen?.folio_nuevo === ticket.folio)?.folio ?? null;

        ticket.detalle.forEach((v) => {
          ws.addRow({
            folio: ticket.folio,
            venta_id: v.venta_id,
            fecha_hora_venta: formatFecha(v.fecha_hora_venta),
            producto: v.producto,
            cantidad: v.cantidad,
            precio_unitario: Number(v.precio_unitario),
            total_venta: Number(v.total_venta),
            forma_pago: v.forma_pago,
            punto_venta: v.punto_venta,
            tipo: v.tipo_punto_venta,
            transferencia: '',
          });
        });

        const saldoActual = esTransferido ? 0 : Number(ticket.resumen.saldo_actual);
        let transferenciaTexto = '';
        if (esTransferido) {
          transferenciaTexto = 'TRANSFERIDO';
        } else if ((ticket.resumen.transferido ?? 0) > 0) {
          transferenciaTexto = folioOrigen
            ? `Transferido del Folio ${folioOrigen}: $${Number(ticket.resumen.transferido).toFixed(2)}`
            : `Transferido: $${Number(ticket.resumen.transferido).toFixed(2)}`;
        }

        const resumenRow = ws.addRow({
          folio: `RESUMEN ${ticket.folio}`,
          venta_id: '',
          fecha_hora_venta: formatFecha(ticket.resumen.fecha_primera_venta),
          producto: ticket.resumen.evento ?? '',
          cantidad: '',
          precio_unitario: Number(ticket.resumen.saldo_recarga_caja),
          total_venta: Number(ticket.resumen.total_consumido_puesto),
          forma_pago: `Saldo: $${saldoActual.toFixed(2)}`,
          punto_venta: '',
          tipo: '',
          transferencia: transferenciaTexto,
        });
        resumenRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        resumenRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: esTransferido ? 'FFFF8C24' : 'FF1A1A1A' } };

        if (esTransferido) {
          const transCell = resumenRow.getCell('transferencia');
          transCell.alignment = { horizontal: 'center' };
        }

        ws.addRow({});
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_ventas.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar Excel:', err);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-xl-row">
      <div className="col-xl-12">
        <div className="card mb-5">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bolder fs-3 mb-1">Filtros de Reporte</span>
              <span className="text-muted mt-1 fw-bold fs-7">
                Seleccione uno o varios días administrativos para visualizar las ventas
              </span>
            </h3>
          </div>
          <div className="card-body py-3">
            <div className="row">
              <div className="col-lg-4">
                <label className="fw-bold fs-6 mb-2">Días Administrativos</label>
                <Select
                  value={diasSeleccionados}
                  isLoading={cargandoDias}
                  options={listadosDeDias}
                  styles={styleSelect}
                  theme={themeSelect}
                  classNamePrefix="tc-select"
                  placeholder="Seleccione uno o varios días..."
                  isMulti
                  closeMenuOnSelect={false}
                  onChange={(opts: any) => {
                    const selected = opts ?? [];
                    setDiasSeleccionados(selected);
                    setIdsDia(selected.map((d: any) => d.value as number));
                  }}
                  isClearable
                  noOptionsMessage={() => 'Sin opciones'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-6 mb-xl-9">
          <div className="card-header border-0 pt-5">
            <div className="card-title">
              <h3 className="card-label fw-bolder text-dark">Reporte de Ventas</h3>
            </div>
            <div className="card-toolbar">
              {list.length > 0 && (
                <button
                  className="btn btn-sm btn-light-success"
                  disabled={loadingDownload}
                  onClick={handleDownloadExcel}
                >
                  {loadingDownload ? (
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Descargando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    'Descargar Excel'
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="card-body pt-3">
            {idsDia.length === 0 ? (
              <div className="text-center text-muted py-10 fs-6">
                Seleccione al menos un día administrativo para ver el reporte.
              </div>
            ) : loadingRequest ? (
              <LoadingGrid text="Cargando reporte..." />
            ) : list.length === 0 ? (
              <div className="text-center text-muted py-10 fs-6">
                No se encontraron ventas para el día seleccionado.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>ID Venta</th>
                      <th>Fecha / Hora</th>
                      <th>Producto</th>
                      <th className="text-center">Cant.</th>
                      <th className="text-end">Precio Unit.</th>
                      <th className="text-end">Total</th>
                      <th className="text-center">Forma Pago</th>
                      <th>Punto de Venta</th>
                      <th>Tipo</th>
                      <th className="text-center">Transferencia</th>
                    </tr>
                  </thead>
                  {list.map((ticket: TicketConVentas) => {
                    const esTransferido = ticket.resumen?.activo === 0 && !!ticket.resumen?.folio_nuevo;
                    const folioOrigen = list.find(t => t.resumen?.folio_nuevo === ticket.folio)?.folio ?? null;
                    return (
                      <tbody
                        key={ticket.folio}
                        style={{ outline: `1px solid ${esTransferido ? 'var(--tc-primary)' : 'var(--tc-border)'}`, outlineOffset: '-1px' }}
                      >
                        <tr className="tc-report-group-header">
                          <td
                            colSpan={10}
                            className="fw-bolder fs-6 ps-3 py-2"
                          >
                            🎫 Folio: {ticket.folio}
                            {ticket.resumen?.evento && (
                              <span className="fw-normal fs-7 ms-3" style={{ color: 'var(--tc-text-muted)' }}>
                                — {ticket.resumen.evento}
                              </span>
                            )}
                          </td>
                        </tr>
                        <TablaDetalle detalle={ticket.detalle} />
                        <FilaResumen resumen={ticket.resumen} folioOrigen={folioOrigen} />
                        <tr>
                          <td colSpan={10} style={{ height: '12px', padding: 0 }} />
                        </tr>
                      </tbody>
                    );
                  })}
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { Vista };
